import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import { ENVIRONMENTS } from '../engine/GameEngine';
import { loadHighScore, loadCoinBalance, canClaimDailyReward, getDailyRewardAmount, loadDailyReward, saveDailyReward, saveCoinBalance, loadUnlockedItems } from '../engine/GameState';

export default function StartScreen({ onStart, onOpenShop, onOpenMissions, onOpenLeaderboard, selectedEnv, onEnvChange }) {
  const [highScore] = useState(loadHighScore);
  const [coinBalance, setCoinBalance] = useState(loadCoinBalance);
  const [showDailyReward, setShowDailyReward] = useState(false);
  const [dailyRewardAmount, setDailyRewardAmount] = useState(0);
  const [streak, setStreak] = useState(0);
  const [claimed, setClaimed] = useState(false);
  const [unlocked] = useState(loadUnlockedItems);

  useEffect(() => {
    if (canClaimDailyReward()) {
      const { streak: s } = loadDailyReward();
      const newStreak = s + 1;
      setStreak(newStreak);
      setDailyRewardAmount(getDailyRewardAmount(newStreak));
      setShowDailyReward(true);
    }
  }, []);

  const handleClaimReward = () => {
    const { streak: s } = loadDailyReward();
    const newStreak = s + 1;
    const amount = getDailyRewardAmount(newStreak);
    const newBalance = coinBalance + amount;
    setCoinBalance(newBalance);
    saveCoinBalance(newBalance);
    saveDailyReward({ lastClaim: new Date()?.toISOString(), streak: newStreak });
    setClaimed(true);
    setTimeout(() => setShowDailyReward(false), 1500);
  };

  const envList = Object.entries(ENVIRONMENTS);

  return (
    <div
      className="relative flex flex-col items-center justify-center min-h-full p-6"
      style={{
        background: 'linear-gradient(135deg, #0f0c29, #302b63, #24243e)',
        minHeight: 500,
      }}
    >
      {/* Animated background particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 20 })?.map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full animate-pulse"
            style={{
              width: Math.random() * 6 + 2,
              height: Math.random() * 6 + 2,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              background: `rgba(${Math.random() > 0.5 ? '99,102,241' : '245,158,11'},${Math.random() * 0.6 + 0.2})`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${Math.random() * 2 + 1}s`,
            }}
          />
        ))}
      </div>
      {/* Logo */}
      <div className="relative z-10 text-center mb-6">
        <div className="text-6xl mb-2 animate-bounce">🐒</div>
        <h1 className="font-heading font-black text-4xl md:text-5xl text-white mb-1" style={{ textShadow: '0 0 30px rgba(99,102,241,0.8)' }}>
          Monkey Ride
        </h1>
        <p className="text-lg font-bold" style={{ color: '#f59e0b', textShadow: '0 0 15px rgba(245,158,11,0.6)' }}>3D</p>
        <p className="font-caption text-sm mt-1" style={{ color: 'rgba(255,255,255,0.6)' }}>Endless Runner Adventure</p>
      </div>
      {/* Stats row */}
      <div className="relative z-10 flex gap-4 mb-6">
        <div className="text-center px-4 py-2 rounded-xl" style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)' }}>
          <div className="flex items-center gap-1.5 justify-center">
            <Icon name="Trophy" size={14} color="#f59e0b" />
            <span className="font-data text-sm font-bold text-white">{highScore?.toLocaleString()}</span>
          </div>
          <p className="font-caption text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.5)' }}>Best Score</p>
        </div>
        <div className="text-center px-4 py-2 rounded-xl" style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)' }}>
          <div className="flex items-center gap-1.5 justify-center">
            <span className="text-sm">🪙</span>
            <span className="font-data text-sm font-bold text-white">{coinBalance?.toLocaleString()}</span>
          </div>
          <p className="font-caption text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.5)' }}>Coins</p>
        </div>
      </div>
      {/* Environment selector */}
      <div className="relative z-10 w-full max-w-sm mb-5">
        <p className="font-caption text-xs font-semibold mb-2 text-center" style={{ color: 'rgba(255,255,255,0.6)' }}>SELECT WORLD</p>
        <div className="grid grid-cols-5 gap-1.5">
          {envList?.map(([key, env]) => (
            <button
              key={key}
              onClick={() => onEnvChange?.(key)}
              className="py-2 px-1 rounded-lg text-center transition-all"
              style={{
                background: selectedEnv === key ? 'rgba(99,102,241,0.5)' : 'rgba(255,255,255,0.08)',
                border: selectedEnv === key ? '2px solid rgba(129,140,248,0.8)' : '1px solid rgba(255,255,255,0.15)',
                transform: selectedEnv === key ? 'scale(1.05)' : 'scale(1)',
              }}
            >
              <div className="text-lg mb-0.5">
                {key === 'jungle' ? '🌿' : key === 'city' ? '🏙️' : key === 'beach' ? '🏖️' : key === 'snow' ? '❄️' : '🚀'}
              </div>
              <p className="font-caption text-xs" style={{ color: 'rgba(255,255,255,0.7)', fontSize: 9 }}>
                {env.name?.split(' ')?.[0]}
              </p>
            </button>
          ))}
        </div>
      </div>
      {/* Play button */}
      <div className="relative z-10 w-full max-w-xs mb-4">
        <button
          onClick={onStart}
          className="w-full py-4 rounded-2xl font-heading font-black text-xl text-white transition-all active:scale-95 hover:scale-105"
          style={{
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            boxShadow: '0 0 30px rgba(99,102,241,0.6), 0 4px 20px rgba(0,0,0,0.4)',
          }}
        >
          🎮 PLAY NOW
        </button>
      </div>
      {/* Action buttons */}
      <div className="relative z-10 grid grid-cols-3 gap-2 w-full max-w-xs mb-4">
        {[
          { icon: '🛒', label: 'Shop', action: onOpenShop },
          { icon: '🎯', label: 'Missions', action: onOpenMissions },
          { icon: '🏆', label: 'Leaderboard', action: onOpenLeaderboard },
        ]?.map(({ icon, label, action }) => (
          <button
            key={label}
            onClick={action}
            className="py-3 rounded-xl text-center transition-all hover:scale-105 active:scale-95"
            style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)' }}
          >
            <div className="text-xl mb-0.5">{icon}</div>
            <p className="font-caption text-xs text-white">{label}</p>
          </button>
        ))}
      </div>
      {/* Daily reward popup */}
      {showDailyReward && (
        <div className="absolute inset-0 z-30 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div
            className="p-6 rounded-2xl text-center max-w-xs w-full mx-4"
            style={{ background: 'linear-gradient(135deg, #1a1a3e, #2d2d5e)', border: '2px solid rgba(245,158,11,0.6)' }}
          >
            <div className="text-5xl mb-3">🎁</div>
            <h3 className="font-heading font-bold text-xl text-white mb-1">Daily Reward!</h3>
            <p className="font-caption text-sm mb-1" style={{ color: 'rgba(255,255,255,0.6)' }}>Day {streak} streak</p>
            <div className="flex items-center justify-center gap-2 my-3">
              <span className="text-2xl">🪙</span>
              <span className="font-data text-3xl font-black" style={{ color: '#f59e0b' }}>+{dailyRewardAmount}</span>
            </div>
            {!claimed ? (
              <button
                onClick={handleClaimReward}
                className="w-full py-3 rounded-xl font-bold text-white transition-all hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}
              >
                Claim Reward!
              </button>
            ) : (
              <div className="py-3 text-green-400 font-bold">✅ Claimed!</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
